import { supabase } from '../config/supabaseClient';

// Types
export interface RecruitmentJob {
  id?: string;
  user_id?: string;
  organization_id?: string | null;
  title: string;
  description?: string;
  responsibilities?: string[];
  requirements?: string[];
  nice_to_haves?: string[];
  department?: string;
  location?: string;
  remote_type?: 'onsite' | 'hybrid' | 'remote';
  employment_type?: 'full-time' | 'part-time' | 'contract' | 'internship';
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  benefits?: string[];
  hiring_manager_id?: string;
  positions_available?: number;
  experience_level?: 'entry' | 'mid' | 'senior' | 'executive';
  status?: 'draft' | 'active' | 'paused' | 'closed' | 'filled';
  posted_at?: string;
  closed_at?: string;
  posted_to_career_site?: boolean;
  posted_boards?: any;
  external_urls?: any;
  custom_application_form?: any;
  screening_questions?: any;
  auto_reject_criteria?: any;
  created_at?: string;
  updated_at?: string;
}

export interface Candidate {
  id?: string;
  user_id?: string;
  organization_id?: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  location?: string;
  current_company?: string;
  current_title?: string;
  linkedin_url?: string;
  portfolio_url?: string;
  github_url?: string;
  resume_url?: string;
  resume_text?: string;
  skills?: string[];
  experience_years?: number;
  education?: any;
  work_history?: any;
  ai_summary?: string;
  ai_skills_extracted?: string[];
  ai_score?: number;
  in_talent_pool?: boolean;
  talent_pool_tags?: string[];
  source?: string;
  email_opt_in?: boolean;
  sms_opt_in?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Application {
  id?: string;
  job_id: string;
  candidate_id: string;
  user_id?: string;
  resume_url?: string;
  cover_letter?: string;
  screening_answers?: any;
  application_source?: string;
  referrer_id?: string;
  current_stage?: 'applied' | 'screening' | 'phone_screen' | 'technical' | 'interview' | 'final_round' | 'offer' | 'hired' | 'rejected' | 'withdrawn';
  stage_changed_at?: string;
  rating?: number;
  ai_match_score?: number;
  ai_highlights?: string[];
  ai_concerns?: string[];
  status?: 'active' | 'rejected' | 'withdrawn' | 'hired';
  rejection_reason?: string;
  withdrawn_reason?: string;
  applied_at?: string;
  viewed_at?: string;
  last_activity_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Interview {
  id?: string;
  application_id: string;
  user_id?: string;
  interview_type?: 'phone_screen' | 'video' | 'onsite' | 'technical' | 'panel';
  scheduled_date: string;
  duration_minutes?: number;
  location?: string;
  video_link?: string;
  meeting_room?: string;
  interviewer_ids?: string[];
  panel_members?: any;
  questions?: any;
  scorecard_template_id?: string;
  status?: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  cancellation_reason?: string;
  reminder_sent_at?: string;
  confirmation_sent_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Offer {
  id?: string;
  application_id: string;
  job_id: string;
  user_id?: string;
  job_title: string;
  department?: string;
  start_date?: string;
  salary: number;
  salary_currency?: string;
  bonus?: number;
  equity?: string;
  benefits?: string[];
  employment_type?: 'full-time' | 'part-time' | 'contract';
  contract_length_months?: number;
  probation_months?: number;
  offer_letter_url?: string;
  contract_url?: string;
  signed_offer_url?: string;
  status?: 'draft' | 'sent' | 'accepted' | 'declined' | 'expired';
  sent_at?: string;
  expires_at?: string;
  responded_at?: string;
  decline_reason?: string;
  requires_approval?: boolean;
  approved_by?: string;
  approved_at?: string;
  created_at?: string;
  updated_at?: string;
}

// ==================== JOBS ====================

export const getJobs = async (): Promise<RecruitmentJob[]> => {
  const { data, error } = await supabase
    .from('recruitment_jobs')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

export const getJob = async (id: string): Promise<RecruitmentJob> => {
  const { data, error } = await supabase
    .from('recruitment_jobs')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

export const createJob = async (job: RecruitmentJob): Promise<RecruitmentJob> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  
  const { data, error } = await supabase
    .from('recruitment_jobs')
    .insert({ ...job, user_id: user.id })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateJob = async (id: string, updates: Partial<RecruitmentJob>): Promise<RecruitmentJob> => {
  const { data, error } = await supabase
    .from('recruitment_jobs')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const deleteJob = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('recruitment_jobs')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

export const publishJob = async (id: string): Promise<RecruitmentJob> => {
  return updateJob(id, { status: 'active', posted_at: new Date().toISOString() });
};

export const closeJob = async (id: string): Promise<RecruitmentJob> => {
  return updateJob(id, { status: 'closed', closed_at: new Date().toISOString() });
};

// ==================== CANDIDATES ====================

export const getCandidates = async (): Promise<Candidate[]> => {
  const { data, error } = await supabase
    .from('recruitment_candidates')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

export const getCandidate = async (id: string): Promise<Candidate> => {
  const { data, error } = await supabase
    .from('recruitment_candidates')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

export const createCandidate = async (candidate: Candidate): Promise<Candidate> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  
  const { data, error } = await supabase
    .from('recruitment_candidates')
    .insert({ ...candidate, user_id: user.id })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateCandidate = async (id: string, updates: Partial<Candidate>): Promise<Candidate> => {
  const { data, error } = await supabase
    .from('recruitment_candidates')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// ==================== APPLICATIONS ====================

export const getApplications = async (jobId?: string): Promise<Application[]> => {
  let query = supabase
    .from('recruitment_applications')
    .select('*, candidate:recruitment_candidates(*), job:recruitment_jobs(*)');
  
  if (jobId) {
    query = query.eq('job_id', jobId);
  }
  
  const { data, error } = await query.order('applied_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

export const getApplication = async (id: string): Promise<Application> => {
  const { data, error } = await supabase
    .from('recruitment_applications')
    .select('*, candidate:recruitment_candidates(*), job:recruitment_jobs(*)')
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

export const createApplication = async (application: Application): Promise<Application> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  
  const { data, error } = await supabase
    .from('recruitment_applications')
    .insert({ ...application, user_id: user.id })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateApplicationStage = async (id: string, stage: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  
  const { error } = await supabase.rpc('update_application_stage', {
    p_application_id: id,
    p_new_stage: stage,
    p_user_id: user.id
  });
  
  if (error) throw error;
};

export const rateApplication = async (id: string, rating: number): Promise<Application> => {
  const { data, error } = await supabase
    .from('recruitment_applications')
    .update({ rating })
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const rejectApplication = async (id: string, reason?: string): Promise<Application> => {
  const { data, error } = await supabase
    .from('recruitment_applications')
    .update({ 
      status: 'rejected', 
      current_stage: 'rejected',
      rejection_reason: reason 
    })
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// ==================== INTERVIEWS ====================

export const getInterviews = async (applicationId?: string): Promise<Interview[]> => {
  let query = supabase
    .from('recruitment_interviews')
    .select('*, application:recruitment_applications(*, candidate:recruitment_candidates(*), job:recruitment_jobs(*))');
  
  if (applicationId) {
    query = query.eq('application_id', applicationId);
  }
  
  const { data, error } = await query.order('scheduled_date', { ascending: true });
  
  if (error) throw error;
  return data || [];
};

export const scheduleInterview = async (interview: Interview): Promise<Interview> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  
  const { data, error } = await supabase
    .from('recruitment_interviews')
    .insert({ ...interview, user_id: user.id })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateInterview = async (id: string, updates: Partial<Interview>): Promise<Interview> => {
  const { data, error } = await supabase
    .from('recruitment_interviews')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// ==================== OFFERS ====================

export const getOffers = async (applicationId?: string): Promise<Offer[]> => {
  let query = supabase
    .from('recruitment_offers')
    .select('*, application:recruitment_applications(*, candidate:recruitment_candidates(*)), job:recruitment_jobs(*)');
  
  if (applicationId) {
    query = query.eq('application_id', applicationId);
  }
  
  const { data, error } = await query.order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

export const createOffer = async (offer: Offer): Promise<Offer> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  
  const { data, error } = await supabase
    .from('recruitment_offers')
    .insert({ ...offer, user_id: user.id })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const sendOffer = async (id: string): Promise<Offer> => {
  const { data, error } = await supabase
    .from('recruitment_offers')
    .update({ 
      status: 'sent', 
      sent_at: new Date().toISOString() 
    })
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// ==================== CONVERSIONS ====================

export const convertToStaff = async (applicationId: string): Promise<string> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  
  const { data, error } = await supabase.rpc('convert_candidate_to_staff', {
    p_application_id: applicationId,
    p_user_id: user.id
  });
  
  if (error) throw error;
  return data; // Returns staff_id
};

// ==================== ANALYTICS ====================

export const getRecruitmentAnalytics = async () => {
  const { data: jobs } = await supabase
    .from('recruitment_jobs')
    .select('id, status, created_at');
  
  const { data: applications } = await supabase
    .from('recruitment_applications')
    .select('id, current_stage, applied_at, status, application_source');
  
  const { data: offers } = await supabase
    .from('recruitment_offers')
    .select('id, status, created_at, sent_at, responded_at');
  
  return {
    totalJobs: jobs?.length || 0,
    activeJobs: jobs?.filter(j => j.status === 'active').length || 0,
    totalApplications: applications?.length || 0,
    applicationsByStage: applications?.reduce((acc, app) => {
      acc[app.current_stage] = (acc[app.current_stage] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    applicationsBySource: applications?.reduce((acc, app) => {
      const source = app.application_source || 'Unknown';
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    totalOffers: offers?.length || 0,
    acceptedOffers: offers?.filter(o => o.status === 'accepted').length || 0,
  };
};

// Notes
export interface RecruitmentNote {
  id?: string;
  application_id: string;
  user_id?: string;
  note: string;
  is_private?: boolean;
  created_at?: string;
  updated_at?: string;
}

export const getNotes = async (applicationId: string): Promise<RecruitmentNote[]> => {
  const { data, error } = await supabase
    .from('recruitment_notes')
    .select('*')
    .eq('application_id', applicationId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const createNote = async (note: Omit<RecruitmentNote, 'id' | 'created_at' | 'updated_at'>): Promise<RecruitmentNote> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  const { data, error } = await supabase
    .from('recruitment_notes')
    .insert({
      ...note,
      user_id: user?.id,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateNote = async (id: string, updates: Partial<RecruitmentNote>): Promise<RecruitmentNote> => {
  const { data, error } = await supabase
    .from('recruitment_notes')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteNote = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('recruitment_notes')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// Activity Log
export interface ActivityLog {
  id?: string;
  application_id?: string;
  user_id?: string;
  action_type: string;
  description: string;
  metadata?: any;
  created_at?: string;
}

export const getActivityLog = async (applicationId: string): Promise<ActivityLog[]> => {
  const { data, error } = await supabase
    .from('recruitment_activity_log')
    .select('*')
    .eq('application_id', applicationId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};
